import React, { createContext, Dispatch, SetStateAction, useCallback, useState } from 'react'
import { omitBy, isUndefined } from '@golden/utils'
import { FinanceDepositWayGroupForm, FinanceDepositWayGroupSearchQuery } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import EditFinanceDepositWayGroupForm, { EditFinanceDepositWayGroupFormType, initialForm } from '../../../components/admin/finance/EditFinanceDepositWayGroupForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useGetDataByParams, useRedirectHandleBack, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import allRoute, { Path } from '../../../components/admin/route/route'
import { searchToRequest } from './FinanceDepositWayPage'
import StateProvider from '../../../providers/default/StateProvider'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import { usePageFlow } from '../../../utils/default/PageFlowHook'

const formToRequest: (form: EditFinanceDepositWayGroupFormType) => FinanceDepositWayGroupForm = (form) => {
  const converted = {
    name: form.name,
    is_active: form.isActive,
    method: form.method,
    support_accounts: form.supportAccounts,
    support_ways: form.supportWays
  } as FinanceDepositWayGroupForm
  return omitBy(converted, isUndefined) as FinanceDepositWayGroupForm
}

export const SearchStateContext = createContext<[FinanceDepositWayGroupSearchQuery, Dispatch<SetStateAction<FinanceDepositWayGroupSearchQuery>>]>([{}, () => {}])

const FinanceDepositWayGroupCreatePage: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const gdk = useGDK()
  const { t } = useT()
  const pageFlow = usePageFlow()

  const [id, setId] = useState<number>(0)
  const [defaultForm, setDefaulForm] = useState<EditFinanceDepositWayGroupFormType>(initialForm())

  useGetDataByParams({
    path: Path.FINANCE_DEPOSIT_WAY_GROUP_UPDATE,
    gdkFunc: (param: { id: string }) => gdk.finance.getDepositWayGroup(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setId(res.id)
      setDefaulForm(initialForm({
        name: res.name,
        isActive: res.is_active,
        cashMin: Number(res.cash_min),
        cashMax: Number(res.cash_max),
        method: res.method
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: allRoute.financeDepositWayGroup.encodePath({ search: { ...request }, param: {} })
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceDepositWayGroupFormType, FinanceDepositWayGroupForm, null>({
    dialogId: 'updateWayGroup',
    globalDialog,
    getChangeDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdate')}
        </React.Fragment>
      )
    }), [t, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updateDepositWayGroup(id, payload),
    gdkFuncDependencies: [gdk, id],
    afterSuccessDialog: handleBack
  })

  return (
    <StateProvider
      context={SearchStateContext}
      defaultValue={{}}
    >
      <Box padding={5}>
        <LoadingAndErrorFrame { ...pageFlow.status } >
          <EditFinanceDepositWayGroupForm
            title={t('common.updateDepositWayGroup')}
            okText={t('common.confirmUpdate')}
            onBack={handleDebouncedBack}
            onSubmit={handleSubmit}
            defaultValue={defaultForm}
            searchContext={SearchStateContext}
            pageFlow={pageFlow}
            groupId={id}
          />
        </LoadingAndErrorFrame>
      </Box>
    </StateProvider>
  )
}

export default React.memo(FinanceDepositWayGroupCreatePage)
